import type { RelatedFrame } from "@/hooks/useTaskRelatedFrames";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconTarget,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

// ~3 fps, the badge camera's capture rate.
const PLAY_INTERVAL_MS = 333;

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

interface Props {
  frames: RelatedFrame[];
  frameIdx: number;
  queryIdx: number;
  onChange: (idx: number) => void;
}

/**
 * Scrubber over a task's parcel frames: prev/next, a slider with a tick at
 * the task frame, ~3 fps playback, and a jump back to the task frame.
 * Hotkeys: `,` / `.` step, `P` play/pause (none collide with
 * coverage-beta-hotkeys.json).
 */
export const FrameScrubber = ({
  frames,
  frameIdx,
  queryIdx,
  onChange,
}: Props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const last = frames.length - 1;
  const loadedCount = frames.filter((f) => f.status === "loaded").length;

  const step = (delta: number) =>
    onChange(Math.max(0, Math.min(last, frameIdx + delta)));

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      // Skip frames that failed; hold on one that's still downloading.
      let nextIdx = frameIdx;
      for (let i = 0; i < frames.length; i++) {
        nextIdx = nextIdx >= last ? 0 : nextIdx + 1;
        if (frames[nextIdx].status !== "error") break;
      }
      if (frames[nextIdx].status === "loaded") onChange(nextIdx);
    }, PLAY_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isPlaying, frameIdx, last, frames, onChange]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey)
        return;
      if (e.key === ",") {
        setIsPlaying(false);
        onChange(Math.max(0, frameIdx - 1));
      } else if (e.key === ".") {
        setIsPlaying(false);
        onChange(Math.min(last, frameIdx + 1));
      } else if (e.key === "p" || e.key === "P") {
        setIsPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [frameIdx, last, onChange]);

  const buttonClass =
    "flex size-7 items-center justify-center rounded text-gray-700 hover:bg-gray-200 disabled:opacity-40";

  return (
    <div
      className="absolute bottom-3 left-1/2 z-10 flex w-[min(520px,80%)] -translate-x-1/2 items-center gap-1.5 rounded-md border border-gray-300 bg-gray-100/95 px-2 py-1 shadow-md"
      // Keep slider drags from also panning the zoomable image underneath.
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        className={buttonClass}
        onClick={() => setIsPlaying((p) => !p)}
        title={isPlaying ? "Pause (P)" : "Play (P)"}
      >
        {isPlaying ? (
          <IconPlayerPauseFilled size={16} />
        ) : (
          <IconPlayerPlayFilled size={16} />
        )}
      </button>
      <button
        className={buttonClass}
        disabled={frameIdx === 0}
        onClick={() => {
          setIsPlaying(false);
          step(-1);
        }}
        title="Previous frame (,)"
      >
        <IconChevronLeft size={16} />
      </button>

      <div className="relative flex-1">
        <input
          type="range"
          aria-label="Frame selector"
          className="w-full"
          min={0}
          max={last}
          value={frameIdx}
          onChange={(e) => {
            setIsPlaying(false);
            onChange(Number(e.target.value));
          }}
        />
        {/* Tick marking the task's own frame. */}
        <div
          className="pointer-events-none absolute -top-1 h-2 w-0.5 -translate-x-1/2 bg-orange-500"
          style={{ left: `${(queryIdx / last) * 100}%` }}
        />
      </div>

      <button
        className={buttonClass}
        disabled={frameIdx === last}
        onClick={() => {
          setIsPlaying(false);
          step(1);
        }}
        title="Next frame (.)"
      >
        <IconChevronRight size={16} />
      </button>
      <button
        className={buttonClass}
        disabled={frameIdx === queryIdx}
        onClick={() => {
          setIsPlaying(false);
          onChange(queryIdx);
        }}
        title="Back to task frame"
      >
        <IconTarget size={16} />
      </button>
      <span className="w-16 text-right text-xs font-semibold tabular-nums text-gray-700">
        {frameIdx + 1}/{frames.length}
        {loadedCount < frames.length && (
          <span className="block text-[10px] font-normal text-gray-500">
            {loadedCount} loaded
          </span>
        )}
      </span>
    </div>
  );
};
